import { useState, useContext } from "react";
import { Stack, Typography, Card, Container, Grid, Box, TextField, Button, ToggleButton, ToggleButtonGroup, Snackbar, IconButton, useTheme, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import UploadPhoto from "./common/UploadPhoto";
import Page from "./Page";
import { Context } from "../context";

import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';

const FeedBack = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  const { auth, user } = useContext(Context);
  const isAuth = auth?.authenticated?.isAuth;
  const userDetail = user?.userDetail;
  
  const [reaction, setReaction] = useState(1);
  const [type, setType] = useState("FEEDBACK");
  const [attachment, setAttachment] = useState();
  const [snackBarStatus, setSnackbarStatus] = useState(false);
  const [snackBarMessage, setSnackbarMessage] = useState("Feedback has been submitted successfully");

  const views = [
    { id: 1, icon: SentimentVeryDissatisfiedIcon, color: "error.main", label: "Angry" },
    { id: 2, icon: SentimentDissatisfiedIcon, color: "warning.main", label: "Upset" },
    { id: 3, icon: SentimentNeutralIcon, color: "info.main", label: "Neutral" },
    { id: 4, icon: SentimentSatisfiedAltIcon, color: "success.light", label: "Happy" },
    { id: 5, icon: SentimentVerySatisfiedIcon, color: "success.main", label: "Excited" },
  ];

  const handleCategoryChange = (event, newAlignment) => {
    if (newAlignment !== null) {
      setType(newAlignment);
    }
  };

  const onCancel = () => {
    navigate("/");
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarStatus(false);
  };

  const snackBarAction = (
    <Tooltip title="Dismiss" placement="bottom">
    <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
      <CloseIcon fontSize="small" />
    </IconButton>
    </Tooltip>
  );

  const uploadImage = (binaryString) => {
    if (!binaryString) return;
    let addImagePayload = { image: binaryString };
    (async function () {
      try {
        let response = await axios(`${process.env.REACT_APP_API_END_POINT}util/image-upload`, {
          method: "post",
          data: addImagePayload,
        });
        if (response && response.data) {
          setAttachment(response.data.data);
        }
      } catch (error) {
        console.error(error?.response || error);
      }
    })();
  };

  const submitFeedback = (values, { setSubmitting, resetForm }) => {
    const feedbackData = {
      ...values,
      name: values.email ? `${values.name} (${values.email})` : values.name,
      type: type,
      reaction: reaction,
      attachment: attachment,
    };

    (async function () {
      try {
        let response = await axios(`${process.env.REACT_APP_API_END_POINT}feedback/add`, {
          method: "post",
          data: feedbackData,
        });
        resetForm();
        setReaction(1);
        setAttachment(null);
        setType("FEEDBACK");
        setSnackbarStatus(true);
      } catch (error) {
        console.error(error?.response || error);
      } finally {
        setSubmitting(false);
      }
    })();
  };

  const CurrentReactionIcon = views.find(v => v.id === reaction)?.icon || SentimentVeryDissatisfiedIcon;
  const CurrentReactionColor = views.find(v => v.id === reaction)?.color || "error.main";

  return (
    <Page title="Feedback | ChargeMiles" sx={{ marginTop: { xs: "128px", md: "62px" }, overflow: "hidden", minHeight: '100vh', pb: 10 }}>
      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
        <Card sx={{ 
          boxShadow: (theme) => `0 0 4px 0 rgba(145,158,171,0.24), 0 24px 48px 0 rgba(145,158,171,0.24)`,
          borderRadius: 3,
          overflow: "hidden" 
        }}>
          {/* Header Gradient */}
          <Box
            sx={{
              background: `linear-gradient(135deg, var(--app-color) 0%, ${theme.palette.primary.dark} 100%)`,
              color: '#fff',
              p: { xs: 3, md: 4 },
              textAlign: 'center',
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              We Value Your Feedback
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 500, mx: 'auto' }}>
              Please let us know how we can improve your charging experience.
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 3, md: 5 } }}>
            <Formik
              enableReinitialize
              initialValues={{ comment: "", name: userDetail?.name || "", email: userDetail?.email || "" }}
              validationSchema={Yup.object({
                comment: Yup.string().required("Please compose your feedback"),
                name: Yup.string().required("Please add your corresponding alias"),
                email: Yup.string().email("Invalid email format").required("Please add your email"),
              })}
              onSubmit={submitFeedback}
            >
              {({ values, errors, touched, handleChange, isSubmitting }) => (
                <Form>
                  <Grid container spacing={5}>
                    
                    {/* Rating Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight={600} color="text.secondary" align="center" gutterBottom>
                        Rate Your Experience
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <CurrentReactionIcon sx={{ fontSize: 120, color: CurrentReactionColor, transition: 'color 0.3s ease' }} />
                      </Box>
                      
                      <Stack direction="row" spacing={{ xs: 1, sm: 2 }} justifyContent="center" mb={1}>
                        {views.map((v) => {
                          const IconComponent = v.icon;
                          return (
                            <Box 
                              key={v.id} 
                              onClick={() => setReaction(v.id)}
                              sx={{ 
                                cursor: 'pointer', 
                                p: 1, 
                                borderRadius: '50%', 
                                bgcolor: reaction === v.id ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                transition: 'all 0.2s ease',
                                '&:hover': { transform: 'scale(1.15)' }
                              }}
                              title={v.label}
                            >
                              <IconComponent sx={{ fontSize: 45, color: reaction === v.id ? v.color : 'text.disabled' }} />
                            </Box>
                          );
                        })}
                      </Stack>
                    </Grid>

                    {/* Category Selection */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight={600} color="text.secondary" gutterBottom>
                        Select Category
                      </Typography>
                      <ToggleButtonGroup
                        color="primary"
                        value={type}
                        exclusive
                        onChange={handleCategoryChange}
                        fullWidth
                        orientation={isSmallScreen ? "vertical" : "horizontal"}
                        sx={(theme) => ({ 
                          border: 'none',
                          '& .MuiToggleButtonGroup-grouped': {
                            border: 'none',
                            m: 0
                          },
                          '& .MuiToggleButton-root': { 
                            margin: 0,
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '13px',
                            padding: '12px 16px',
                            minHeight: 44,
                            border: `2px solid ${theme.palette.grey[300]} !important`,
                            color: theme.palette.text.secondary,
                            backgroundColor: theme.palette.background.paper,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden',
                            '&:hover': {
                              background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.light}05 100%)`,
                              borderColor: `${theme.palette.primary.light} !important`,
                              color: theme.palette.primary.main,
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px 0 ${theme.palette.primary.main}20`,
                              zIndex: 1,
                            },
                            '&.Mui-selected': {
                              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%) !important`,
                              color: 'white !important',
                              borderColor: `${theme.palette.primary.main} !important`,
                              boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
                              zIndex: 2,
                              '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%) !important`,
                                boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}60`,
                                transform: 'translateY(-2px)',
                              },
                            },
                            '&:active': {
                              transform: 'translateY(0)',
                            },
                            ...(isSmallScreen ? {
                              // vertical group
                              '&:not(:first-of-type)': {
                                marginTop: '-2px',
                              },
                              '&:first-of-type': {
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 0
                              },
                              '&:last-of-type': {
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0
                              },
                              '&:not(:first-of-type):not(:last-of-type)': {
                                borderRadius: 0
                              }
                            } : {
                              // horizontal group
                              '&:not(:first-of-type)': {
                                marginLeft: '-2px',
                              },
                              '&:first-of-type': {
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0
                              },
                              '&:last-of-type': {
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0
                              },
                              '&:not(:first-of-type):not(:last-of-type)': {
                                borderRadius: 0
                              }
                            })
                          }
                        })}
                      >
                        <ToggleButton value="FEEDBACK">Feedback</ToggleButton>
                        <ToggleButton value="BUG">Bug</ToggleButton>
                        <ToggleButton value="FEATURE">Suggest Feature</ToggleButton>
                        <ToggleButton value="LOCATION">Report Point Issues</ToggleButton>
                      </ToggleButtonGroup>
                    </Grid>

                    {/* Input Fields */}
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={2} sx={{ height: '100%' }}>
                        <TextField
                          fullWidth
                          name="name"
                          label="Your Name"
                          disabled={isAuth}
                          variant="outlined"
                          value={values.name}
                          onChange={handleChange}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isAuth ? 'action.hover' : 'background.paper' } }}
                        />
                        <TextField
                          fullWidth
                          name="email"
                          label="Your Email"
                          disabled={isAuth}
                          variant="outlined"
                          value={values.email}
                          onChange={handleChange}
                          error={Boolean(touched.email && errors.email)}
                          helperText={touched.email && errors.email}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isAuth ? 'action.hover' : 'background.paper' } }}
                        />
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <UploadPhoto
                        setImageSrc={setAttachment}
                        imageSrc={attachment}
                        uploadImage={uploadImage}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        name="comment"
                        label="Please describe your thoughts or issue here..."
                        variant="outlined"
                        value={values.comment}
                        onChange={handleChange}
                        error={Boolean(touched.comment && errors.comment)}
                        helperText={touched.comment && errors.comment}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                      />
                    </Grid>

                    {/* Actions */}
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                        <Button 
                          variant="outlined" 
                          onClick={onCancel}
                          size="large"
                          sx={{ px: 4, borderRadius: 2, color: 'text.secondary', borderColor: 'divider' }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          type="submit" 
                          variant="contained" 
                          size="large"
                          disabled={isSubmitting}
                          sx={{ 
                            px: 5, 
                            borderRadius: 2,
                            background: `linear-gradient(135deg, var(--app-color) 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: `0 8px 16px 0 rgba(25, 118, 210, 0.24)`,
                            '&:hover': {
                              boxShadow: `0 8px 16px 0 rgba(25, 118, 210, 0.5)`,
                            }
                          }}
                        >
                          {isSubmitting ? "Submitting..." : "Submit Feedback"}
                        </Button>
                      </Box>
                    </Grid>
                    
                  </Grid>
                </Form>
              )}
            </Formik>
          </Box>
        </Card>
      </Container>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={snackBarStatus}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackBarMessage}
        action={snackBarAction}
      />
    </Page>
  );
};

export default FeedBack;
